import { KeyIcon } from "@heroicons/react/24/solid"
import { useState } from "react"
import { toast } from "react-toastify"

import {
  useGetMetaBrandCredentialQuery,
  useRemoveMetaBrandCredentialMutation,
  useSetMetaBrandCredentialEditsMutation,
  useSetMetaBrandCredentialPausedMutation,
  type MetaBrandCredentialStatus,
} from "~/ui-rtk/api/metaBrandCredentialApi"
// Direct import (not the `controls` barrel).
import Button from "~/ui-rtk/components/ui/controls/Button"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"

import {
  META_ADVANCED_BADGE,
  META_ADVANCED_EDITS_OFF_CONFIRM,
  META_ADVANCED_EDITS_ON_CONFIRM,
  META_ADVANCED_HELP,
  META_ADVANCED_HELP_TIER_ORDER,
  META_ADVANCED_IN_USE,
  META_ADVANCED_NOT_USED,
  META_ADVANCED_PAUSE_CONFIRM,
  META_ADVANCED_PAUSE_ERROR,
  META_ADVANCED_PAUSED,
  META_ADVANCED_PAUSED_TOAST,
  META_ADVANCED_REMOVE_CONFIRM,
  META_ADVANCED_REMOVE_CONFIRM_TIER_ORDER,
  META_ADVANCED_RESUME_CONFIRM,
  META_ADVANCED_RESUME_ERROR,
  META_ADVANCED_RESUMED_TOAST,
  META_ADVANCED_TITLE,
  metaAdvancedInvalidLine,
  withNextCredential,
} from "./copy"
import MetaAdvancedTokenDialog from "./MetaAdvancedTokenDialog"

const TITLE_ID = "meta-advanced-title"

/** "Not set", or the system user and the day it was set (UTC, as the agency credential dialog shows dates). */
function statusLineOf(status: MetaBrandCredentialStatus): string {
  if (!status.connected) return "Not set"
  const systemUser = `System user ${status.systemUserName ?? "(name unknown)"}`
  return status.setAt
    ? `${systemUser} · set on ${status.setAt.slice(0, 10)}`
    : systemUser
}

/** Where the company's Meta edits run: here (since the day the switch went on), or on the login. */
function editsLineOf(status: MetaBrandCredentialStatus): string {
  if (!status.editsEnabled) return "Meta edits: Instagram Insights login"
  return status.editsEnabledAt
    ? `Meta edits: Meta Advanced since ${status.editsEnabledAt.slice(0, 10)}`
    : "Meta edits: Meta Advanced"
}

/**
 * Tier order on (APP-566 T3): whether the brand's Meta reads and edits run on
 * the system user now. Null while Meta refuses its token and it is not paused:
 * the refusal line says it.
 */
function tierStatusLineOf(status: MetaBrandCredentialStatus): string | null {
  const accountUserNext = status.accountUserNext === true
  if (status.pausedAt) {
    return withNextCredential(META_ADVANCED_PAUSED, accountUserNext)
  }
  if (status.invalidAt) return null
  if (status.systemUserSkip) {
    return `${withNextCredential(META_ADVANCED_NOT_USED, accountUserNext)} ${status.systemUserSkip.detail}`
  }
  return META_ADVANCED_IN_USE
}

/**
 * Meta Advanced (APP-548): the brand's Meta system-user credential, as a row
 * of the connector list. SourceItems renders it right after Facebook
 * Marketing, for super admins only; brand users never see it. It looks like a
 * connector row but is not a connector: the token lives in its own table.
 * Set / Replace open the token dialog; Remove asks for a confirmation. Once
 * set, the switch moves the brand's Meta edits here or back to the login,
 * also after a confirmation. With the tier order on (APP-566 T3) the switch is
 * gone: a status line, Pause / Resume and the refusal line.
 */
export default function MetaAdvancedItem() {
  const { data: status, isLoading, isError } = useGetMetaBrandCredentialQuery()
  const companyId = useCurrentCompany()?.id
  const [removeCredential, { isLoading: removing }] =
    useRemoveMetaBrandCredentialMutation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [setEdits, { isLoading: movingEdits }] =
    useSetMetaBrandCredentialEditsMutation()
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [confirmEdits, setConfirmEdits] = useState(false)
  const [setPaused, { isLoading: pausing }] =
    useSetMetaBrandCredentialPausedMutation()
  const [confirmPause, setConfirmPause] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const connected = status?.connected === true
  const editsEnabled = status?.editsEnabled === true
  const tierOrder = status?.tierOrder === true
  // APP-566 T5: the account user comes next after the system user.
  const accountUserNext = status?.accountUserNext === true
  const paused = status?.pausedAt != null
  const tierStatusLine = status && tierOrder ? tierStatusLineOf(status) : null

  const togglePause = async () => {
    if (!companyId) return
    setError(null)
    try {
      await setPaused({ companyId, paused: !paused }).unwrap()
      setConfirmPause(false)
      toast.success(
        paused
          ? META_ADVANCED_RESUMED_TOAST
          : withNextCredential(META_ADVANCED_PAUSED_TOAST, accountUserNext),
      )
    } catch (err) {
      setError(
        getServerErrorMessage(
          err,
          paused ? META_ADVANCED_RESUME_ERROR : META_ADVANCED_PAUSE_ERROR,
        ),
      )
    }
  }

  const moveEdits = async () => {
    if (!companyId) return
    setError(null)
    try {
      await setEdits({ companyId, enabled: !editsEnabled }).unwrap()
      setConfirmEdits(false)
      toast.success(
        editsEnabled
          ? "Meta edits now run on the Instagram Insights login."
          : "Meta edits now run on Meta Advanced.",
      )
    } catch (err) {
      setError(
        getServerErrorMessage(err, "Couldn't move the Meta edits. Try again."),
      )
    }
  }

  const remove = async () => {
    if (!companyId) return
    setError(null)
    try {
      await removeCredential({ companyId }).unwrap()
      setConfirmRemove(false)
      toast.success("Meta Advanced credential removed.")
    } catch (err) {
      setError(
        getServerErrorMessage(
          err,
          "Couldn't remove the Meta Advanced credential. Try again.",
        ),
      )
    }
  }

  return (
    <li
      data-testid="meta-advanced-item"
      aria-labelledby={TITLE_ID}
      className="flex justify-between w-full p-[12px] rounded-xl relative overflow-visible border border-basic-blue bg-[#222446]"
    >
      <div className="flex min-w-0 items-start gap-system-sm relative z-10">
        <div className="flex h-system-2xl w-system-2xl shrink-0 items-center justify-center rounded-lg bg-basic-dark-blue">
          <KeyIcon className="h-6 w-6 text-white" aria-hidden="true" />
        </div>
        <div className="text-left flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p id={TITLE_ID} className="font-medium leading-5 text-white">
              {META_ADVANCED_TITLE}
            </p>
            <div className="flex items-center gap-1 px-2 py-1 bg-system-gray-600 rounded-full">
              <span className="text-[10px] text-white leading-none font-bold tracking-wide">
                {META_ADVANCED_BADGE}
              </span>
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-300">
            {tierOrder
              ? withNextCredential(
                  META_ADVANCED_HELP_TIER_ORDER,
                  accountUserNext,
                )
              : META_ADVANCED_HELP}
          </p>

          {isLoading && <p className="text-sm text-gray-300">Loading…</p>}
          {isError && (
            <p className="text-sm text-states-error">
              Couldn&apos;t load the Meta Advanced status.
            </p>
          )}

          {status && !isError && (
            <>
              <div className="flex flex-wrap items-center gap-1">
                <p className="break-words text-sm text-white">
                  {statusLineOf(status)}
                </p>
                {connected && !confirmRemove && (
                  <>
                    <span className="text-gray-500 text-sm">•</span>
                    <button
                      type="button"
                      onClick={() => {
                        setError(null)
                        setConfirmEdits(false)
                        setConfirmPause(false)
                        setConfirmRemove(true)
                      }}
                      className="text-gray-400 hover:text-gray-200 text-sm underline cursor-pointer transition-colors"
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
              {status.missingScopes.length > 0 && (
                <p className="break-words text-sm text-system-warning-300">
                  {`Missing permissions: ${status.missingScopes.join(", ")}; the matching features stay off.`}
                </p>
              )}
              {status.lastError && (
                <p className="text-sm text-states-error">{`Last problem: ${status.lastError}`}</p>
              )}

              {connected && !tierOrder && (
                <div className="flex flex-wrap items-center gap-1">
                  <p className="break-words text-sm text-white">
                    {editsLineOf(status)}
                  </p>
                  {!confirmEdits && (
                    <>
                      <span className="text-gray-500 text-sm">•</span>
                      <button
                        type="button"
                        onClick={() => {
                          setError(null)
                          setConfirmRemove(false)
                          setConfirmPause(false)
                          setConfirmEdits(true)
                        }}
                        className="text-gray-400 hover:text-gray-200 text-sm underline cursor-pointer transition-colors"
                      >
                        {editsEnabled ? "Move edits back" : "Move edits here"}
                      </button>
                    </>
                  )}
                </div>
              )}

              {connected && !tierOrder && confirmEdits && (
                <div
                  role="alertdialog"
                  aria-label="Confirm the Meta edits move"
                  className="mt-2 flex flex-col gap-2 rounded-md border border-basic-blue bg-basic-dark-blue px-system-md py-system-sm text-sm text-white"
                >
                  <span>
                    {editsEnabled
                      ? META_ADVANCED_EDITS_OFF_CONFIRM
                      : META_ADVANCED_EDITS_ON_CONFIRM}
                  </span>
                  <div className="flex justify-end gap-2">
                    <Button
                      size="xs"
                      variant={{ variant: "text" }}
                      onClick={() => {
                        setError(null)
                        setConfirmEdits(false)
                      }}
                      disabled={movingEdits}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="xs"
                      variant={{ variant: "solid", color: "pink" }}
                      onClick={() => void moveEdits()}
                      isLoading={movingEdits}
                    >
                      {editsEnabled ? "Move edits back" : "Move edits here"}
                    </Button>
                  </div>
                </div>
              )}

              {connected && tierOrder && (
                <>
                  <div className="flex flex-wrap items-center gap-1">
                    {tierStatusLine && (
                      <p className="break-words text-sm text-white">
                        {tierStatusLine}
                      </p>
                    )}
                    {!confirmPause && (
                      <>
                        {tierStatusLine && (
                          <span className="text-gray-500 text-sm">•</span>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setError(null)
                            setConfirmRemove(false)
                            setConfirmEdits(false)
                            setConfirmPause(true)
                          }}
                          className="text-gray-400 hover:text-gray-200 text-sm underline cursor-pointer transition-colors"
                        >
                          {paused ? "Resume" : "Pause"}
                        </button>
                      </>
                    )}
                  </div>
                  {status.invalidAt && (
                    <p className="break-words text-sm text-states-error">
                      {withNextCredential(
                        metaAdvancedInvalidLine(status.invalidAt.slice(0, 10)),
                        accountUserNext,
                      )}
                    </p>
                  )}
                </>
              )}

              {connected && tierOrder && confirmPause && (
                <div
                  role="alertdialog"
                  aria-label={paused ? "Confirm resume" : "Confirm pause"}
                  className="mt-2 flex flex-col gap-2 rounded-md border border-basic-blue bg-basic-dark-blue px-system-md py-system-sm text-sm text-white"
                >
                  <span>
                    {paused
                      ? META_ADVANCED_RESUME_CONFIRM
                      : withNextCredential(
                          META_ADVANCED_PAUSE_CONFIRM,
                          accountUserNext,
                        )}
                  </span>
                  <div className="flex justify-end gap-2">
                    <Button
                      size="xs"
                      variant={{ variant: "text" }}
                      onClick={() => {
                        setError(null)
                        setConfirmPause(false)
                      }}
                      disabled={pausing}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="xs"
                      variant={{ variant: "solid", color: "pink" }}
                      onClick={() => void togglePause()}
                      isLoading={pausing}
                    >
                      {paused ? "Resume" : "Pause"}
                    </Button>
                  </div>
                </div>
              )}

              {connected && confirmRemove && (
                <div
                  role="alertdialog"
                  aria-label="Confirm removal"
                  className="mt-2 flex flex-col gap-2 rounded-md border border-system-red-600 bg-system-red-900/30 px-system-md py-system-sm text-sm text-system-red-100"
                >
                  <span>
                    {tierOrder
                      ? withNextCredential(
                          META_ADVANCED_REMOVE_CONFIRM_TIER_ORDER,
                          accountUserNext,
                        )
                      : META_ADVANCED_REMOVE_CONFIRM}
                  </span>
                  <div className="flex justify-end gap-2">
                    <Button
                      size="xs"
                      variant={{ variant: "text" }}
                      onClick={() => {
                        setError(null)
                        setConfirmRemove(false)
                      }}
                      disabled={removing}
                    >
                      Keep it
                    </Button>
                    <Button
                      size="xs"
                      variant={{ variant: "solid", color: "red" }}
                      onClick={() => void remove()}
                      isLoading={removing}
                    >
                      Remove credential
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {error && (
            <p role="alert" className="mt-1 text-sm text-states-error">
              {error}
            </p>
          )}
        </div>
      </div>

      {status && !isError && (
        <div className="flex md:items-center sm:items-center gap-1 relative z-10">
          <Button
            className="w-[82px] h-[40px] cursor-pointer p-0"
            variant={{ variant: "solid", color: "pink" }}
            onClick={() => {
              setError(null)
              setConfirmRemove(false)
              setConfirmEdits(false)
              setConfirmPause(false)
              setDialogOpen(true)
            }}
            disabled={removing || movingEdits || pausing}
          >
            <p className="text-white text-[12px] font-bold leading-[145%]">
              {connected ? "Replace" : "Set"}
            </p>
          </Button>
        </div>
      )}

      {dialogOpen && companyId && (
        <MetaAdvancedTokenDialog
          companyId={companyId}
          replacing={connected}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </li>
  )
}
